import { FC } from "react";
import { useForm, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useRecoilValue } from "recoil";

import {
  capitalize,
  currencySignSelector,
  IAreaPriceAdditionDto,
  isEqualNumberArraysAnyOrder,
  Log,
  safeNumber,
} from "@ryde/shared";

import {
  ColorIconButton,
  MultiSelectBoundInputByControl,
  NumberInputByControl,
} from "../../../controls";
import { IAreaAdditionItemForm } from "./area-addition.models";
import { AreaAdditionEditItemStyled } from "./area-addition.styled";
import { parseStartingFromAreaIndexInput } from "./area-addition.utils";

export const AreaAdditionItemEdit: FC<IAreaAdditionItemForm> = ({
  index,
  value,
  onCancel,
  setValue: setValueParentForm,
  getValues: getValuesParentForm,
  fieldName,
  isNew,
}) => {
  const { t } = useTranslation();
  const { watch } = useFormContext();
  const {
    handleSubmit,
    control,
    formState: { isValid },
  } = useForm<Partial<IAreaPriceAdditionDto>>({
    defaultValues: value,
    mode: "all",
  });

  const currencySign = useRecoilValue(currencySignSelector);

  const sourceAreaWatchedId = watch("sourceAreaId");
  const destinationAreaWatchedId = watch("destinationAreaId");

  const excludeAreaIds = [sourceAreaWatchedId, destinationAreaWatchedId].filter(
    Boolean,
  ) as number[];

  const updateValuesHandler = handleSubmit(data => {
    const targetField = `${fieldName ?? ""}.${index || 0}`;

    if (!data?.price || Number(data.price) <= 0) {
      Log.warnAndToast(t("errors.invalidNumber"));
      return;
    }

    const { value: startingFromAreaIndex, isInvalid: isInvalidAreaIndex } =
      parseStartingFromAreaIndexInput(data.startingFromAreaIndex);

    if (isInvalidAreaIndex) {
      Log.warnAndToast(
        t("priceRate.AREA_ADDITION_SEQUENCE_MUST_BE_WHOLE_NUMBER"),
      );
      return;
    }

    // a rule with neither areas nor a sequence number can never match anything
    if (!data.areaIds?.length && !startingFromAreaIndex) {
      Log.warnAndToast(t("priceRate.AREA_ADDITION_REQUIRES_AREAS_OR_SEQUENCE"));
      return;
    }

    const dataNormalized: IAreaPriceAdditionDto = {
      price: Number(data.price),
      areaIds: data.areaIds?.map(areaId => Number(areaId)) || [],
      // omitted when empty: an absent index keeps the rule on the pre-steps behavior
      ...(startingFromAreaIndex !== undefined && { startingFromAreaIndex }),
    };

    const existingPriceItems: IAreaPriceAdditionDto[] =
      getValuesParentForm?.(fieldName);
    const priceItemsExcludeCurrent =
      typeof index === "number" && existingPriceItems?.length
        ? existingPriceItems.toSpliced(index, 1)
        : existingPriceItems || [];

    const combinationExists = priceItemsExcludeCurrent.some(priceItem => {
      const isSameAreaIds = isEqualNumberArraysAnyOrder(
        priceItem.areaIds,
        dataNormalized.areaIds,
      );
      const isSameStartingFromAreaIndex =
        safeNumber(priceItem.startingFromAreaIndex) ===
        safeNumber(dataNormalized.startingFromAreaIndex);

      return isSameAreaIds && isSameStartingFromAreaIndex;
    });

    if (isNew && combinationExists) {
      Log.warnAndToast(t("general.ITEM_ALREADY_EXISTS_MESSAGE"));
      return;
    }

    Log.debug(
      `Set value for availability item target field:  ${targetField}`,
      data,
    );
    setValueParentForm?.(targetField, dataNormalized, {
      shouldValidate: true,
      shouldDirty: true,
    });
    onCancel?.();
  });

  return (
    <AreaAdditionEditItemStyled>
      {/* the sequence number means two things (full UI explainers arrive with RYDE-7946):
          - no areas selected → step: charge every chargeable inner area from this position up
          - with areas → count gate: the combination applies only from this many inner areas */}
      <NumberInputByControl
        name="startingFromAreaIndex"
        control={control}
        formLabel={t("priceRate.formLabel.startingFromAreaIndex")}
        formAttributes={{ numberInput: { minValue: 0 } }}
      />

      {/* areas are optional since RYDE-7997: an area-less rule with a sequence number is a step */}
      <MultiSelectBoundInputByControl
        name={"areaIds"}
        control={control}
        formLabel={capitalize(t("area.plural"))}
        formAttributes={{
          dropItemsSource: "area",
          customDropdownItemsFilter: excludeAreaIds?.length
            ? item => {
                if (!item?.id) {
                  return false;
                }
                const excludeAreaIdsSet = new Set(excludeAreaIds);
                return !excludeAreaIdsSet.has(item.id as number);
              }
            : undefined,
        }}
      />

      <div className="buttons_edit_view">
        <NumberInputByControl
          name={"price"}
          control={control}
          formLabel={`${currencySign} ${t("general.PRICE")}`}
          formValidation={{
            type: "number",
            rules: [
              {
                validation: "required",
              },
            ],
          }}
        />
        <ColorIconButton
          iconType="CHECK"
          edge="end"
          aria-label="save"
          disabled={!isValid}
          onClick={updateValuesHandler}
        />
        <ColorIconButton
          iconType="CANCEL"
          edge="end"
          aria-label="cancel"
          onClick={() => onCancel?.()}
        />
      </div>
    </AreaAdditionEditItemStyled>
  );
};
